import * as React from 'react';
import debounce from 'lodash/debounce';
import type { Dimension } from 'src/types';
import { useGlobalStore } from '../use-app-wide-store';

/**
 * useElementSize hook - returns the offsetWidth and offsetHeight of an element
 */
export const useElementSize = <T extends HTMLElement = HTMLDivElement>(): readonly [
  (node: T | null) => void,
  Dimension,
] => {
  // App has a drawer, so we need to listen to the drawer resize event.
  const prefersWideDashboardMenu = useGlobalStore((state) => state.prefersWideDashboardMenu);
  const prefersWideDashboardMenuRef = React.useRef<boolean>(false);

  const elementRef = React.useRef<T | null>(null);

  const [size, setSize] = React.useState<Dimension>({
    width: 0,
    height: 0,
  });

  const elementRefFn = React.useCallback((node: T | null) => {
    if (!node?.offsetWidth || !node?.offsetHeight) return;

    elementRef.current = node;

    setSize({
      width: node.offsetWidth,
      height: node.offsetHeight,
    });
  }, []);

  // Prevent too many rendering using useCallback
  const handleSize = React.useCallback(() => {
    if (!elementRef.current?.offsetWidth || !elementRef.current?.offsetHeight) return;

    setTimeout(() => {
      const newSize = { width: elementRef.current?.offsetWidth || 0, height: elementRef.current?.offsetHeight || 0 };

      setSize(newSize);
    }, 300);
  }, []);

  React.useEffect(() => {
    window.addEventListener('resize', debounce(handleSize, 300));

    return () => {
      window.removeEventListener('resize', debounce(handleSize, 300));
    };
  }, [handleSize]);

  React.useEffect(() => {
    if (prefersWideDashboardMenuRef.current === prefersWideDashboardMenu) return;
    prefersWideDashboardMenuRef.current = prefersWideDashboardMenu;
    handleSize();
  }, [prefersWideDashboardMenu, handleSize]);

  return [elementRefFn, size] as const;
};
